import { PodcastData, PodcastFeedResponse } from '@/components-2.0/contentful/ChannelGuide/interfaces';
import { useState, useRef, useCallback } from 'react';

const DEFAULT_PAGE_SIZE = 20;
const PATH = '/podcastfeed';

type Status = 'pending' | 'loading' | 'success' | 'error';

export function useGetPodcasts(cmsId: string, pageSize = DEFAULT_PAGE_SIZE) {
    const [podcasts, setPodcasts] = useState<Array<PodcastData>>([]);
    const [podcastCategory, setPodcastCategory] = useState<string | undefined>();
    const [status, setStatus] = useState<Status>('pending');
    const [totalPodcastCount, setTotalPodcastCount] = useState<number>(0);

    const pageRef = useRef(0);

    const resetPodcasts = useCallback(
        (pagesToKeep: number = 0) => {
            if (!pagesToKeep) {
                setStatus('pending');
            }
            setPodcasts((prev) => prev.slice(0, pagesToKeep * pageSize));
            pageRef.current = pagesToKeep;
        },
        [pageSize],
    );

    const fetchPodcasts = useCallback(
        async (nextCategory?: string) => {
            if (status === 'loading') return;

            setStatus('loading');

            let url = `${PATH}/${cmsId}`;
            if (nextCategory) url += `/${encodeURIComponent(nextCategory)}`;

            const page = pageRef.current;
            const params = new URLSearchParams({
                limit: String(pageSize),
                skip: String(page * pageSize),
            });

            const res = await fetch(`${url}?${params.toString()}`);

            if (!res.ok) {
                setPodcasts([]);
                setTotalPodcastCount(0);
                setStatus('error');
                return;
            }

            const json = (await res.json()) as PodcastFeedResponse;

            pageRef.current += 1;

            setPodcasts((prev) => [...prev, ...(json.podcasts ?? [])]);
            setTotalPodcastCount(json.total ?? 0);
            setStatus('success');
        },
        [cmsId, status, pageSize],
    );

    const fetchPodcastsFirstPage = useCallback(
        (category?: string) => {
            setPodcasts([]);
            pageRef.current = 0;
            fetchPodcasts(category);
        },
        [fetchPodcasts],
    );

    const canLoadMorePodcasts = podcasts.length > 0 && podcasts.length < totalPodcastCount;

    const loadMorePodcasts = useCallback(() => {
        if (!canLoadMorePodcasts) return;
        return fetchPodcasts(podcastCategory);
    }, [canLoadMorePodcasts, fetchPodcasts, podcastCategory]);

    const onChangePodcastCategory = useCallback(
        (category?: string) => {
            setPodcastCategory(category);
            fetchPodcastsFirstPage(category);
        },
        [fetchPodcastsFirstPage],
    );

    return {
        podcasts,
        podcastCategory,
        onChangePodcastCategory,
        totalPodcastCount,
        resetPodcasts,
        fetchPodcastsFirstPage,
        loadMorePodcasts,
        canLoadMorePodcasts,
        status,
        isPendingPodcasts: status === 'pending',
        isLoadingPodcasts: status === 'loading',
        isSuccessPodcasts: status === 'success',
        isErrorPodcasts: status === 'error',
    };
}
